import { trackConversion } from "@/lib/trafficSource";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useEffect } from "react";
import { reportSecurityEvent } from "@/lib/securityReporter";
import { messagesChannelFor } from "@/lib/realtimeChannels";

// ---------------------------------------------------------------------------
// Realtime subscription deduplication
// ---------------------------------------------------------------------------
// Multiple components may mount `useConversationMessages(conversationId)` at
// the same time (e.g. message thread + unread badge + preview pane). Without
// dedup, each mount opens its own Realtime channel for the same conversation,
// multiplying server-side fan-out and producing duplicate INSERT handlers.
//
// A module-level registry keyed by `${userId}:${conversationId}` owns at most
// ONE Supabase Realtime channel per (user, conversation) pair. Subscribers
// register a listener; the channel is created on the first subscriber and
// torn down when the last subscriber unmounts.
type MessageListener = (newRow: any) => void;

interface DedupedSubscription {
  channel: ReturnType<typeof supabase.channel>;
  listeners: Set<MessageListener>;
  refCount: number;
}

const conversationSubs = new Map<string, DedupedSubscription>();

function subscribeToConversation(
  userId: string,
  conversationId: string,
  listener: MessageListener,
): () => void {
  const key = `${userId}:${conversationId}`;
  let entry = conversationSubs.get(key);

  if (!entry) {
    const listeners = new Set<MessageListener>();
    const channel = supabase
      .channel(messagesChannelFor(conversationId), { config: { private: true } })
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          for (const fn of listeners) fn(payload.new);
        },
      )
      .subscribe((status) => {
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          reportSecurityEvent({
            event_type: "realtime_denied",
            severity: "error",
            resource: `messages:${conversationId}`,
            action: "subscribe",
            message: `Realtime subscription rejected: ${status}`,
            client_context: { hook: "useConversationMessages", status },
          });
        }
      });

    entry = { channel, listeners, refCount: 0 };
    conversationSubs.set(key, entry);
  }

  entry.listeners.add(listener);
  entry.refCount += 1;

  return () => {
    const current = conversationSubs.get(key);
    if (!current) return;
    current.listeners.delete(listener);
    current.refCount -= 1;
    if (current.refCount <= 0) {
      supabase.removeChannel(current.channel);
      conversationSubs.delete(key);
    }
  };
}

// Exposed for tests.
export const __conversationSubsForTesting = conversationSubs;

export interface Conversation {
  id: string;
  listing_id: string;
  buyer_id: string;
  seller_id: string;
  created_at: string;
  updated_at: string;
  listing_title?: string;
  other_user_name?: string;
  last_message?: string;
  last_message_at?: string;
  unread_count?: number;
}

export function useConversations() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["conversations", user?.id],
    enabled: !!user,
    queryFn: async () => {
      // Get conversations
      const { data: convos, error } = await supabase
        .from("conversations")
        .select("*")
        .or(`buyer_id.eq.${user!.id},seller_id.eq.${user!.id}`)
        .order("updated_at", { ascending: false });

      if (error) throw error;
      if (!convos || convos.length === 0) return [];

      // Enrich with listing titles, other user names, last messages
      const listingIds = [...new Set(convos.map((c) => c.listing_id))];
      const otherUserIds = [...new Set(convos.map((c) => c.buyer_id === user!.id ? c.seller_id : c.buyer_id))];

      const [listingsRes, profilesRes] = await Promise.all([
        supabase.rpc("get_conversation_listing_titles" as any, { _ids: listingIds }),
        supabase.rpc("get_conversation_partner_profiles" as any, { _user_ids: otherUserIds }),
      ]);

      const listingMap = new Map(((listingsRes.data as any[]) ?? []).map((l) => [l.id, l.title]));
      const profileMap = new Map(
        ((profilesRes.data as any[]) ?? []).map((p) => [p.user_id, p.full_name || p.company_name || "User"])
      );

      // Get last message and unread count for each conversation
      const enriched: Conversation[] = await Promise.all(
        convos.map(async (c) => {
          const [lastMsgRes, unreadRes] = await Promise.all([
            supabase
              .from("messages")
              .select("content, created_at")
              .eq("conversation_id", c.id)
              .order("created_at", { ascending: false })
              .limit(1)
              .single(),
            supabase
              .from("messages")
              .select("id", { count: "exact", head: true })
              .eq("conversation_id", c.id)
              .eq("read", false)
              .neq("sender_id", user!.id),
          ]);

          const otherId = c.buyer_id === user!.id ? c.seller_id : c.buyer_id;

          return {
            ...c,
            listing_title: listingMap.get(c.listing_id) || "Listing",
            other_user_name: profileMap.get(otherId) || "User",
            last_message: lastMsgRes.data?.content,
            last_message_at: lastMsgRes.data?.created_at,
            unread_count: unreadRes.count ?? 0,
          };
        })
      );

      return enriched;
    },
  });

  return query;
}

export function useConversationMessages(conversationId: string | null) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["messages", conversationId],
    enabled: !!conversationId && !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("messages")
        .select("*")
        .eq("conversation_id", conversationId!)
        .order("created_at", { ascending: true });

      if (error) throw error;

      // Mark unread messages as read
      if (data && data.length > 0) {
        const unread = data.filter((m) => !m.read && m.sender_id !== user!.id);
        if (unread.length > 0) {
          await supabase
            .from("messages")
            .update({ read: true })
            .in("id", unread.map((m) => m.id));
        }
      }

      return data ?? [];
    },
  });

  // Real-time subscription (deduplicated per user+conversation)
  useEffect(() => {
    if (!conversationId || !user?.id) return;

    const unsubscribe = subscribeToConversation(user.id, conversationId, (newRow) => {
      queryClient.setQueryData(
        ["messages", conversationId],
        (old: any[] | undefined) => {
          const list = old ?? [];
          // Guard against duplicate inserts if multiple listeners somehow fire
          if (list.some((m) => m.id === newRow.id)) return list;
          return [...list, newRow];
        },
      );
      // Mark as read if from other user
      if (newRow.sender_id !== user.id) {
        supabase
          .from("messages")
          .update({ read: true })
          .eq("id", newRow.id)
          .then(() => {});
      }
      // Refresh conversations list for unread counts
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    });

    return unsubscribe;
  }, [conversationId, user?.id, queryClient]);

  return query;
}

export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      senderId,
      content,
    }: {
      conversationId: string;
      senderId: string;
      content: string;
    }) => {
      const { error } = await supabase.from("messages").insert({
        conversation_id: conversationId,
        sender_id: senderId,
        content,
      });
      if (error) throw error;

      // Update conversation updated_at
      await supabase
        .from("conversations")
        .update({ updated_at: new Date().toISOString() })
        .eq("id", conversationId);
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

export function useStartConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      listingId,
      buyerId,
      sellerId,
      initialMessage,
    }: {
      listingId: string;
      buyerId: string;
      sellerId: string;
      initialMessage: string;
    }) => {
      // Check if conversation already exists
      const { data: existing } = await supabase
        .from("conversations")
        .select("id")
        .eq("listing_id", listingId)
        .eq("buyer_id", buyerId)
        .single();

      let conversationId: string;

      if (existing) {
        conversationId = existing.id;
      } else {
        const { data: newConvo, error } = await supabase
          .from("conversations")
          .insert({ listing_id: listingId, buyer_id: buyerId, seller_id: sellerId })
          .select("id")
          .single();
        if (error) throw error;
        conversationId = newConvo.id;
        trackConversion("inquiry");
      }

      // Send the initial message
      const { error: msgError } = await supabase.from("messages").insert({
        conversation_id: conversationId,
        sender_id: buyerId,
        content: initialMessage,
      });
      if (msgError) throw msgError;

      return conversationId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

export function useUnreadCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["unread-count", user?.id],
    enabled: !!user,
    refetchInterval: 30000,
    queryFn: async () => {
      // Get all conversation IDs for this user
      const { data: convos } = await supabase
        .from("conversations")
        .select("id")
        .or(`buyer_id.eq.${user!.id},seller_id.eq.${user!.id}`);

      if (!convos || convos.length === 0) return 0;

      const { count } = await supabase
        .from("messages")
        .select("id", { count: "exact", head: true })
        .in("conversation_id", convos.map((c) => c.id))
        .eq("read", false)
        .neq("sender_id", user!.id);

      return count ?? 0;
    },
  });
}
